/**
 * F17.2.11 — Visualization panel Controsoffitto (5 tab uniformi al wall).
 *
 * Layout mirror di VisualizationPanel (wall):
 *  - Tabs: Sezione | 3D | Pianta riflessa | Costi | Tecnico
 *  - Box prestazioni in fondo (EI / Rw / λ / Peso + spessore pacchetto)
 *
 * Differenze vs wall:
 *  - "Pianta" → "Pianta riflessa" (vista dal basso del controsoffitto)
 *  - Niente gate "view3d" per ora (la 3D ceiling è G2 leggera, free per tutti i piani)
 *  - "Spessore parete" → "Pacchetto sospeso" nel box prestazioni
 */
import React, { Suspense, useMemo } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Ruler, Map, Receipt, FileText, Box, Image as ImageIcon, Loader2,
  Anchor, Wrench, Banknote, Calculator,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { rasterizeSvg } from './tech-view/exportTechnicalSheetPDF';
import type { LayerV2 } from '../types';
import type {
  CeilingSectionModel,
  CeilingBlock,
} from './section-view/ceilingModel';
import type { CeilingCostBreakdown } from '../hooks/computeCeilingCosts';
import type { CeilingDimensionalState } from '../hooks/useCeilingConfiguratorState';
import TechnicalCeilingDrawing from './section-view/TechnicalCeilingDrawing';
import CeilingPlanView from './plan-view/CeilingPlanView';
import CeilingTechnicalSheet from './tech-view/CeilingTechnicalSheet';
import { formatNumber } from '@/utils/format';

// Lazy-load del 3D (Three.js + R3F + drei = ~1MB)
const CeilingView3D = React.lazy(() => import('./photoreal-3d/CeilingView3D'));

interface Props {
  layers: LayerV2[];
  ceiling: CeilingDimensionalState;
  model: CeilingSectionModel;
  breakdown: CeilingCostBreakdown;
  ceilingTitle: string | null;
  /** Nome stratigrafia (per export PNG/PDF) */
  stratigraphyName?: string;
  /** Descrizione (per export PDF futuro) */
  stratigraphyDescription?: string;
}

const CeilingVisualizationShell: React.FC<Props> = ({
  layers,
  ceiling,
  model,
  breakdown,
  ceilingTitle,
  stratigraphyName,
  stratigraphyDescription,
}) => {
  // ===== Aggregati per il box prestazioni (in fondo) =====
  const totalWeight = useMemo(() => layers.reduce((acc, l) => {
    const w = Number(l.material?.weight_per_sqm ?? 0);
    return acc + (Number.isFinite(w) ? w : 0);
  }, 0), [layers]);

  const lambdaLayers = layers.filter(l => Number(l.material?.thermal_conductivity ?? 0) > 0);
  const avgLambda = lambdaLayers.length > 0
    ? lambdaLayers.reduce((acc, l) => {
        const lam = Number(l.material?.thermal_conductivity ?? 0);
        const t = Number(l.thickness ?? l.material?.thickness ?? 0);
        return acc + lam * t;
      }, 0) / lambdaLayers.reduce((acc, l) => acc + Number(l.thickness ?? l.material?.thickness ?? 0), 0)
    : null;

  const bestRw = layers.reduce((acc, l) => {
    const r = Number(l.material?.acoustic_performance ?? 0);
    return r > acc ? r : acc;
  }, 0);

  const fireClasses = Array.from(new Set(
    layers
      .map(l => l.material?.fire_class)
      .filter((c): c is string => typeof c === 'string' && c.trim() !== '')
  ));

  const handleExportSectionPng = async () => {
    const svg = document.querySelector<SVGSVGElement>(
      '[data-ceiling-section-view] svg, .technical-ceiling-svg',
    );
    if (!svg) {
      toast.error('Sezione non trovata.');
      return;
    }
    const dataUrl = await rasterizeSvg(svg, 2);
    if (!dataUrl) {
      toast.error('Errore durante la generazione del PNG.');
      return;
    }
    const a = document.createElement('a');
    const safe = (stratigraphyName ?? 'controsoffitto')
      .replace(/[^a-z0-9-_ ]/gi, '_')
      .trim() || 'controsoffitto';
    a.href = dataUrl;
    a.download = `${safe}-sezione.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success('PNG scaricato.');
  };

  return (
    <div className="space-y-4">
      <Tabs defaultValue="section" className="w-full" data-tour-id="ceiling-viz-tabs">
        <TabsList className="w-full grid grid-cols-5">
          <TabsTrigger value="section"><Ruler className="h-4 w-4 mr-1.5" /> Sezione</TabsTrigger>
          <TabsTrigger value="iso"><Box className="h-4 w-4 mr-1.5" /> Vista 3D</TabsTrigger>
          <TabsTrigger value="plan"><Map className="h-4 w-4 mr-1.5" /> Pianta riflessa</TabsTrigger>
          <TabsTrigger value="costs"><Receipt className="h-4 w-4 mr-1.5" /> Costi</TabsTrigger>
          <TabsTrigger value="tech"><FileText className="h-4 w-4 mr-1.5" /> Tecnico</TabsTrigger>
        </TabsList>

        {/* === Tab Sezione === */}
        <TabsContent value="section" className="mt-4">
          <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
            {ceilingTitle && <Badge variant="secondary">{ceilingTitle}</Badge>}
            <Button
              size="sm"
              variant="outline"
              disabled={layers.length === 0}
              onClick={handleExportSectionPng}
              className="ml-auto"
            >
              <ImageIcon className="h-4 w-4 mr-1.5" />
              Esporta PNG
            </Button>
          </div>
          {model.blocks.length <= 1 ? (
            <Card className="p-12 text-center text-sm text-muted-foreground border-dashed min-h-[420px] flex items-center justify-center">
              Aggiungi layer per vedere la sezione
            </Card>
          ) : (
            <div data-ceiling-section-view>
              <TechnicalCeilingDrawing layers={layers} ceiling={ceiling} />
            </div>
          )}
        </TabsContent>

        {/* === Tab Vista 3D === */}
        <TabsContent value="iso" className="mt-4">
          <Suspense
            fallback={
              <Card className="p-12 text-center text-sm text-muted-foreground border-dashed min-h-[420px] flex items-center justify-center">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground/60" />
                  Caricamento motore 3D...
                </div>
              </Card>
            }
          >
            <CeilingView3D layers={layers} ceiling={ceiling} stratigraphyName={stratigraphyName} />
          </Suspense>
        </TabsContent>

        {/* === Tab Pianta riflessa === */}
        <TabsContent value="plan" className="mt-4">
          <CeilingPlanView layers={layers} ceiling={ceiling} />
          <p className="text-xs text-muted-foreground mt-2 italic">
            ℹ️ Stanza di riferimento 4×5m. Le quantità reali verranno calcolate al &quot;Aggiungi a preventivo&quot;.
          </p>
        </TabsContent>

        {/* === Tab Costi === */}
        <TabsContent value="costs" className="mt-4">
          <CostsTab breakdown={breakdown} />
        </TabsContent>

        {/* === Tab Tecnico === */}
        <TabsContent value="tech" className="mt-4">
          <CeilingTechnicalSheet
            layers={layers}
            ceiling={ceiling}
            model={model}
            ceilingTitle={ceilingTitle}
            stratigraphyName={stratigraphyName}
            stratigraphyDescription={stratigraphyDescription}
            totalCostPerSqm={breakdown.totalCost}
          />
        </TabsContent>
      </Tabs>

      {/* === Schema testuale (fallback collassato sotto le tab) === */}
      {model.blocks.length > 1 && (
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2 font-semibold">
            Schema blocchi
          </div>
          <div className="space-y-1.5">
            {model.blocks.map((block, idx) => (
              <SchemaRow key={idx} block={block} />
            ))}
            <Separator className="my-2" />
            <div className="flex items-center justify-between px-2 text-sm font-medium">
              <span className="text-muted-foreground">Spessore pacchetto</span>
              <span className="tabular-nums">{model.totalDepthMm} mm</span>
            </div>
            {model.isSuspended && model.hangerCountPerSqm !== null && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground px-2 mt-1">
                <Anchor className="h-3 w-3" />
                {formatNumber(model.hangerCountPerSqm, 2)} pendini/m² ·
                {model.hasDoubleStructure ? ' Orditura doppia' : ' Orditura singola'}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* === Box prestazioni (mirror wall) === */}
      <Card className="p-4">
        <div className="grid grid-cols-4 gap-3 text-center">
          {[
            { label: 'EI', value: fireClasses.length > 0 ? fireClasses[0] : '—', sub: 'fuoco' },
            { label: 'Rw', value: bestRw > 0 ? formatNumber(bestRw, 0) : '—', sub: 'fonoiso.' },
            { label: 'λ', value: avgLambda !== null ? formatNumber(avgLambda, 3) : '—', sub: 'termico' },
            { label: 'Peso', value: totalWeight > 0 ? formatNumber(totalWeight, 1) : '—', sub: 'kg/m²' },
          ].map((m) => (
            <div key={m.label}>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{m.label}</div>
              <div className="text-2xl font-semibold text-zinc-700">{m.value}</div>
              <div className="text-[9px] text-muted-foreground">{m.sub}</div>
            </div>
          ))}
        </div>
        <div className="mt-2 pt-2 border-t flex items-center justify-between text-xs text-muted-foreground">
          <span>Pacchetto sospeso</span>
          <span className="font-mono font-medium text-zinc-700">{formatNumber(model.totalDepthMm, 0)} mm</span>
        </div>
        {ceiling.attachmentType === 'suspended' && (
          <div className="flex items-center justify-between text-[10px] text-muted-foreground/70 mt-0.5">
            <span>Camera tecnica</span>
            <span className="font-mono">{formatNumber(ceiling.ceilingDropMm, 0)} mm</span>
          </div>
        )}
      </Card>
    </div>
  );
};

// ============================================================================
// CostsTab — breakdown spostato dal vecchio shell, in Card piena
// ============================================================================

const CostsTab: React.FC<{ breakdown: CeilingCostBreakdown }> = ({ breakdown }) => {
  const euro = (n: number) => `${formatNumber(n, 2)} €`;

  if (breakdown.rows.length === 0) {
    return (
      <Card className="p-12 text-center text-sm text-muted-foreground border-dashed min-h-[300px] flex items-center justify-center">
        <div>
          <Banknote className="h-12 w-12 mx-auto mb-3 text-muted-foreground/40" />
          <p>Aggiungi materiali per vedere i costi</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 mb-3">
        <Banknote className="h-4 w-4 text-zinc-700" />
        <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-900">
          Costi €/m²
        </h3>
      </div>

      <div className="space-y-1.5 text-sm">
        {breakdown.rows.map((row, i) => (
          <div key={i} className="flex items-center justify-between gap-2 py-1">
            <div className="min-w-0 flex-1">
              <div className="truncate">{row.name}</div>
              <div className="text-xs text-muted-foreground">
                {formatNumber(row.quantity, 2)} {row.unit}/m²
                {row.notes && ` · ${row.notes}`}
              </div>
            </div>
            <div className="tabular-nums shrink-0 font-medium">
              {euro(row.rowCost)}
            </div>
          </div>
        ))}

        {breakdown.hangerRow && (
          <div className="flex items-center justify-between gap-2 py-1 border-t pt-2">
            <div className="min-w-0 flex-1">
              <div className="truncate flex items-center gap-1">
                <Anchor className="h-3 w-3" /> {breakdown.hangerRow.hangerName}
              </div>
              <div className="text-xs text-muted-foreground">
                {formatNumber(breakdown.hangerRow.countPerSqm, 2)} pendini/m²
              </div>
            </div>
            <div className="tabular-nums shrink-0 font-medium">
              {euro(breakdown.hangerRow.rowCost)}
            </div>
          </div>
        )}

        <Separator className="my-2" />
        <div className="space-y-1 text-xs text-muted-foreground">
          <div className="flex justify-between"><span>Materiali</span><span className="tabular-nums">{euro(breakdown.subtotalMaterials)}</span></div>
          {breakdown.subtotalScrews > 0 && (
            <div className="flex justify-between"><span>Viti</span><span className="tabular-nums">{euro(breakdown.subtotalScrews)}</span></div>
          )}
          {breakdown.subtotalHangers > 0 && (
            <div className="flex justify-between"><span>Pendini</span><span className="tabular-nums">{euro(breakdown.subtotalHangers)}</span></div>
          )}
          <div className="flex justify-between">
            <span className="flex items-center gap-1"><Wrench className="h-3 w-3" />Manodopera ({formatNumber((breakdown.laborTime * 60), 0)} min)</span>
            <span className="tabular-nums">{euro(breakdown.laborCost)}</span>
          </div>
        </div>
        <Separator className="my-2" />
        <div className="flex items-center justify-between font-semibold">
          <span className="flex items-center gap-1"><Calculator className="h-4 w-4" />Totale €/m²</span>
          <span className="tabular-nums text-lg">{euro(breakdown.totalCost)}</span>
        </div>
        <div className="text-xs text-muted-foreground italic mt-2">
          ℹ️ Il profilo perimetrale e il costo totale per stanza saranno calcolati al momento &quot;Aggiungi a preventivo&quot;
          (in base ad area + perimetro della stanza).
        </div>
      </div>
    </Card>
  );
};

// ============================================================================
// SchemaRow — riga del fallback testuale sotto le tab
// ============================================================================

const blockIcon = (block: CeilingBlock) => {
  switch (block.kind) {
    case 'slab': return '🧱';
    case 'hanger_space': return '⛓️';
    case 'frame': return block.level === 'primary' ? '╤' : '═';
    case 'ceiling_insulation': return '🟧';
    case 'board': return '⬜';
    default: return '·';
  }
};

const blockLabel = (block: CeilingBlock): string => {
  switch (block.kind) {
    case 'slab': return 'Solaio esistente (datum)';
    case 'hanger_space': return block.label;
    case 'frame': return `${block.level === 'primary' ? 'Orditura primaria' : 'Orditura secondaria'} — ${block.label}`;
    case 'ceiling_insulation': return `Isolante (${block.position === 'above_primary' ? 'sopra orditura' : 'vano secondario'}) — ${block.label}`;
    case 'board': return block.label;
    default: return '—';
  }
};

const blockDepth = (block: CeilingBlock): number => {
  switch (block.kind) {
    case 'slab': return block.thicknessMm;
    case 'hanger_space': return block.depthMm;
    case 'frame': return block.depthMm;
    case 'ceiling_insulation': return block.depthMm;
    case 'board': return block.thicknessMm;
    default: return 0;
  }
};

const SchemaRow: React.FC<{ block: CeilingBlock }> = ({ block }) => (
  <div
    className={`
      flex items-center gap-3 px-3 py-2 rounded-lg border text-sm
      ${block.kind === 'slab' ? 'bg-stone-100 border-dashed border-stone-300 text-stone-600' : ''}
      ${block.kind === 'hanger_space' ? 'bg-indigo-50/50 border-indigo-200' : ''}
      ${block.kind === 'frame' ? 'bg-slate-50 border-slate-300' : ''}
      ${block.kind === 'ceiling_insulation' ? 'bg-amber-50 border-amber-200' : ''}
      ${block.kind === 'board' ? 'bg-white border-zinc-200' : ''}
    `}
  >
    <span className="text-lg shrink-0" aria-hidden="true">{blockIcon(block)}</span>
    <div className="flex-1 min-w-0 truncate">{blockLabel(block)}</div>
    <Badge variant="outline" className="shrink-0 tabular-nums text-xs">
      {blockDepth(block)} mm
    </Badge>
  </div>
);

export default CeilingVisualizationShell;
